import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import { IconButton, Tooltip, Menu, MenuItem, Typography } from "@mui/material";
import TableContainer from "@mui/material/TableContainer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import TableBody from "@mui/material/TableBody";
import REACT_APP from "../../environment";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { visuallyHidden } from "@mui/utils";
import UpdateDepartment from "./UpdateDepartment";
import CreateDepartment from "./CreateDepartment";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";
import Loading from "../Loading";
import { Button } from "@material-ui/core";
import Error from "../Error";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EmployeesTable from "./EmployeesTable";
import { useTranslation } from "react-i18next";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: "id", numeric: false, label: "Id" },
    { id: "department_name", numeric: true, label: "Department Name" },
    { id: "branch_name", numeric: true, label: "Branch Name" },
    { id: "head", numeric: true, label: "head" },
    { id: "acting_head", numeric: true, label: "Acting Head" },
    { id: "", numeric: true, label: "" },
    { id: "actions", numeric: true, label: "Action" },
];

function EnhancedTableHead(props) {
    const { t } = useTranslation();
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells
                    .filter((headCell) => sessionStorage.getItem("branch_presence") === "true" || headCell.id !== "branch_name")
                    .map((headCell) => (
                        <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : "asc"}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {t(headCell.label)}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function DepartmentTable() {
    const { t } = useTranslation();
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("calories");
    const [page, setPage] = React.useState(0);
    const [data, setData] = React.useState([]);
    const [idx, setIdx] = React.useState("");
    const [selected, setSelected] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [branches, setBranches] = React.useState([]);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const [anchor, setAnchor] = React.useState(null);

    const openMenu = (event, elem) => {
        setIdx(elem);
        setAnchor(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchor(null);
    };

    React.useEffect(() => {
        getDate();
        getBranches();
    }, []);

    const getDate = async () => {
        setErrorMsg(null);
        setLoading(true);
        axios(`${REACT_APP.URL}/api/hrp/v1/hr/department`, {
            headers: { Authorization: sessionStorage.getItem("token") },
        })
            .then((res) => {
                setData(res.data.data);
                setLoading(false);
            })
            .catch((err) => {
                setErrorMsg(err.message);
                setLoading(false);
            });
    };

    const getBranches = async () => {
        axios(`${REACT_APP.URL}/api/hrp/v1/organization/branches`, {
            headers: { Authorization: sessionStorage.getItem("token") },
        })
            .then((res) => {
                setBranches(res.data.data);
            })
            .catch((err) => {});
    };

    const handleSearch = (search) => {
        axios
            .get(`${REACT_APP.URL}/api/hrp/v1/branch/all?search=${search}`, {
                headers: { Authorization: sessionStorage.getItem("token") },
            })
            .then((res) => {
                setData(res.data.data);
            })
            .catch((err) => {
                setErrorMsg(err.message);
            });
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDelete = () => {
        axios
            .delete(`${REACT_APP.URL}/api/hrp/v1/hr/department/${idx.department_id}`, {
                headers: { Authorization: sessionStorage.getItem("token") },
            })
            .then((res) => {
                setAnchor(null);
                toast.success(t("Success"));
                getDate();
            })
            .catch((err) => {
                toast.error(t("Something went wrong"));
            });
    };

    const navigate = useNavigate();
    const view = (id) => navigate(`/department/${id}`);

    const selectDepartment = (id) => {
        setSelected(id);
    };

    return (
        <>
            {loading ? (
                <Loading />
            ) : errorMsg ? (
                <Error message={errorMsg} />
            ) : (
                <div className="py-5">
                    <div className="d-flex justify-content-between row pb-3">
                        <div className="col-sm-4">
                            <h4 className="text-center text-sm-start">{t("Department")}</h4>
                        </div>
                        <div className="col-sm-4 position-relative">
                            <input
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ paddingLeft: "35px" }}
                                type="text"
                                className="form-control"
                                placeholder={t("Search")}
                            />
                            <SearchIcon sx={{ position: "absolute", top: "8px", left: "20px", color: "#aaa", fontSize: "23px" }} />
                        </div>
                    </div>
                    <Box sx={{ width: "100%" }}>
                        <Paper sx={{ width: "100%", mb: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750, px: 2 }} aria-labelledby="tableTitle" size="medium">
                                    <EnhancedTableHead order={order} onRequestSort={handleRequestSort} orderBy={orderBy} rowCount={data.length} />
                                    <TableBody>
                                        {stableSort(data, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                return (
                                                    <TableRow
                                                        hover
                                                        tabIndex={-1}
                                                        key={row.department_id}
                                                        onDoubleClick={() => selectDepartment(row.department_id)}
                                                        style={{ cursor: "pointer", background: selected === row.department_id ? "rgb(245, 250, 245)" : "" }}
                                                    >
                                                        <TableCell align="left">{row.department_id}</TableCell>
                                                        <TableCell align="left">{row.department_name}</TableCell>
                                                        {sessionStorage.getItem("branch_presence") === "true" && (
                                                            <TableCell align="left">{row.branch_name}</TableCell>
                                                        )}
                                                        <TableCell align="left">
                                                            {row.head?.first_name} {row.head?.last_name}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {row.acting_head?.first_name} {row.acting_head?.last_name}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Button color="primary" onClick={() => view(row.department_id)}>
                                                                {t("details")}
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Box sx={{ flexGrow: 0 }}>
                                                                <Tooltip title={t("Action")}>
                                                                    <IconButton
                                                                        style={{ borderRadius: "50%", padding: "2px" }}
                                                                        onClick={(e, elem = row) => openMenu(e, elem)}
                                                                        sx={{ p: 0 }}
                                                                    >
                                                                        <MoreVertIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Menu
                                                                    sx={{ mt: "23px" }}
                                                                    id="menu-appbar"
                                                                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    anchorEl={anchor}
                                                                    open={Boolean(anchor)}
                                                                    onClose={closeMenu}
                                                                >
                                                                    <MenuItem
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#editDepartment"
                                                                        className="m__item"
                                                                        onClick={() => setAnchor(null)}
                                                                    >
                                                                        <Typography>{t("Update")}</Typography>
                                                                    </MenuItem>
                                                                    {/* <MenuItem className="m__item" onClick={handleDelete}><Typography>Delete</Typography></MenuItem> */}
                                                                </Menu>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                labelRowsPerPage={t("Rows per page")}
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                count={data.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                    <CreateDepartment getDate={getDate} branches={branches} />
                    {idx ? <UpdateDepartment getDate={getDate} data={idx} /> : null}
                    {selected && <EmployeesTable id={selected} />}
                </div>
            )}
        </>
    );
}

export default React.memo(DepartmentTable);
